import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api';
import { alertCopy, lastHappened, type AlertRow } from '../alerts';
import { tr } from '../i18n';
import { useTenant } from '../session';
import { computerStatus } from '../status';
import { Badge, bytes, Button, Card, Empty, ErrorBox, friendlyError, Loading, PageHead, useToast } from '../ui';

function Ring({ value, total }: { value: number; total: number }) {
  const pct = total ? value / total : 0;
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <svg width="132" height="132" viewBox="0 0 132 132" role="img" aria-label={tr('{value} of {total} computers backed up', { value, total })} style={{ flex: 'none' }}>
      <circle cx="66" cy="66" r={r} fill="none" stroke="var(--rule-2)" strokeWidth="10" />
      <circle cx="66" cy="66" r={r} fill="none" stroke={pct === 1 ? 'var(--ok)' : pct >= 0.8 ? 'var(--brass)' : 'var(--bad)'} strokeWidth="10"
        strokeDasharray={`${c * pct} ${c}`} strokeLinecap="round" transform="rotate(-90 66 66)" style={{ transition: 'stroke-dasharray .6s' }} />
      <text x="66" y="72" textAnchor="middle" direction="ltr" style={{ font: '600 28px var(--font-display)', fill: 'var(--ink)' }}>{value}/{total}</text>
    </svg>
  );
}

interface AttentionItem {
  id: string;
  name: string;
  reason: string;
  online: boolean;
  lastSeenAt: string | null;
  lastSuccessAt: string | null;
  policyId: string | null;
  policyName: string | null;
}

export function Dashboard() {
  const t = useTenant();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['dashboard', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/dashboard`), refetchInterval: 30_000 });
  const ten = useQuery({ queryKey: ['tenant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}`) });
  if (q.isLoading) return <Loading rows={6} />;
  if (q.error) return <ErrorBox error={q.error} retry={q.refetch} />;
  const d = q.data;
  const counts = d.devices.counts ?? { backedUp: d.devices.protected, attention: 0, settingUp: 0, paused: 0, blocked: 0 };
  const attention: AttentionItem[] = d.devices.unprotected;
  const total: number = d.devices.total;
  const onboarding: { key: string; done: boolean }[] = d.onboarding;
  const onboardingDone = onboarding.every((s) => s.done);
  const tenant = ten.data?.tenant;
  const quota = Number(d.usage.storage_gb) * 1024 ** 3;
  const usedPct = quota ? Math.min(100, (100 * Number(d.usage.physical)) / quota) : 0;
  const manage = t.can('devices.manage');
  const backUp = async (id: string) => {
    try {
      await post(`/api/t/${t.tenantId}/devices/${id}/backup`);
      toast(tr('Backup requested. The computer starts within a minute.'));
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (e) {
      toast(friendlyError(e), true);
    }
  };
  const steps: Record<string, { label: string; action?: React.ReactNode }> = {
    verify: {
      label: tr('Confirm your email address'),
      action: <Button size="sm" onClick={async () => { await post('/api/auth/verify-email/resend'); toast(tr('We sent the email again.')); }}>{tr('Resend the email')}</Button>,
    },
    enroll: { label: tr('Connect your first computer'), action: manage ? <Link className="btn sm" to={`${t.base}/devices/add`}>{tr('Connect a computer')}</Link> : undefined },
    create_plan: {
      label: tr('Create a backup plan'),
      action: t.can('policies.manage') ? <Link className="btn sm" to={`${t.base}/plans/new`}>{tr('Create a plan')}</Link> : undefined,
    },
    review_plan: {
      label: tr('Check what gets backed up'),
      action: tenant?.default_policy_id ? <Link className="btn sm" to={`${t.base}/plans/${tenant.default_policy_id}`}>{tr('Check the plan')}</Link> : undefined,
    },
    backup: { label: tr('First backup finished') },
    restore: {
      label: tr('Try getting a file back'),
      action: onboarding.find((s) => s.key === 'backup')?.done && t.can('restores.create') ? <Link className="btn sm" to={`${t.base}/restore`}>{tr('Get a file back')}</Link> : undefined,
    },
  };
  const headline =
    total === 0
      ? tr("Let's protect your first computer")
      : attention.length === 0
        ? counts.backedUp === 1 ? tr('Your computer is backed up') : tr('All {n} computers are backed up', { n: counts.backedUp })
        : attention.length === 1 ? tr('1 computer needs attention') : tr('{n} computers need attention', { n: attention.length });
  return (
    <div className="fade-in">
      <PageHead
        eyebrow={t.name}
        title={tr('Overview')}
        actions={
          <>
            {t.can('restores.create') && total > 0 && <Link className="btn" to={`${t.base}/restore`}>{tr('Get files back')}</Link>}
            {manage && <Link className="btn primary" data-tour="add-computer" to={`${t.base}/devices/add`}>{tr('Connect a computer')}</Link>}
          </>
        }
      />
      {tenant?.storage_state === 'failed' && (
        <div className="notice bad" style={{ marginBottom: 16 }}>
          <strong>{tr('We could not prepare your backup storage')}</strong>
          {tr('Our team has been notified. Please contact support if this does not clear up within an hour.')}
        </div>
      )}
      {tenant && tenant.storage_state !== 'ready' && tenant.storage_state !== 'failed' && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <strong>{tr('Preparing your private backup storage…')}</strong>
          {tr('This takes a minute or two. You can already connect computers.')}
        </div>
      )}

      <div className="card card-pad" data-tour="protection" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {total > 0 && <Ring value={counts.backedUp} total={total} />}
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ fontSize: 24 }}>{attention.length === 0 && total > 0 ? '✓ ' : ''}{headline}</h2>
          <p className="muted" style={{ margin: '6px 0 0' }}>
            {total === 0
              ? tr('Connect a computer and it starts backing up on its own. It takes about five minutes.')
              : [
                  counts.settingUp ? tr('{n} still setting up', { n: counts.settingUp }) : '',
                  counts.paused ? tr('{n} with backups paused', { n: counts.paused }) : '',
                  counts.blocked ? tr('{n} blocked', { n: counts.blocked }) : '',
                  d.jobs.running ? tr('{n} backing up right now', { n: d.jobs.running }) : '',
                ].filter(Boolean).join(' · ') || tr('Backups run automatically. There is nothing you need to do.')}
          </p>
          {total === 0 && manage && <Link className="btn primary" style={{ marginTop: 14 }} to={`${t.base}/devices/add`}>{tr('Connect a computer')}</Link>}
        </div>
      </div>

      {!onboardingDone && (
        <Card title={tr('Getting started')} className="" pad>
          <ul className="checklist">
            {onboarding.map((s) => {
              const step = steps[s.key];
              if (!step) return null;
              return (
                <li key={s.key}>
                  <span className={`tick ${s.done ? 'on' : ''}`} aria-hidden="true">{s.done ? '✓' : ''}</span>
                  <span style={{ color: s.done ? 'var(--muted)' : 'inherit' }}>{step.label}{s.done && <span className="sr-only"> ({tr('Done')})</span>}</span>
                  {!s.done && step.action && <span style={{ marginInlineStart: 'auto' }}>{step.action}</span>}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {attention.length > 0 && (
        <Card title={tr('Needs attention')} pad={false}>
          <table>
            <tbody>
              {attention.map((a) => {
                const st = computerStatus({ status: a.reason === 'pending' ? 'pending' : 'active', reason: a.reason, online: a.online, last_success_at: a.lastSuccessAt, last_seen_at: a.lastSeenAt });
                const label = a.reason === 'pending' ? tr('Still setting up') : st.label;
                const hint = a.reason === 'pending' ? tr('Make sure the computer is on and connected to the internet. If this does not clear up, contact support.') : st.hint;
                return (
                  <tr key={a.id}>
                    <td style={{ width: '30%' }}><Link to={`${t.base}/devices/${a.id}`}><strong>{a.name}</strong></Link><div className="small muted">{st.seen}</div></td>
                    <td><Badge tone={st.tone}>{label}</Badge>{hint && <div className="small muted" style={{ marginTop: 4 }}>{hint}</div>}</td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      {a.reason === 'paused' && a.policyId ? (
                        <Link className="btn sm" to={`${t.base}/plans/${a.policyId}`}>{tr('Open the plan')}</Link>
                      ) : (a.reason === 'stale' || a.reason === 'no_recovery_point') && a.online && t.can('jobs.manage') ? (
                        <Button size="sm" onClick={() => backUp(a.id)}>{tr('Back up now')}</Button>
                      ) : (
                        <Link className="btn sm ghost" to={`${t.base}/devices/${a.id}`}>{tr('Open')}</Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <div className="grid g3" style={{ marginTop: 16 }}>
        <div className="card stat">
          <div className="label">{tr('Last 24 hours')}</div>
          <div className="value num">{d.jobs.completed24 + d.jobs.partial24}</div>
          <div className="foot">
            {tr('backups finished')}
            {d.jobs.failed24 > 0 && <> · <span style={{ color: 'var(--bad)' }}>{tr("{n} didn't finish", { n: d.jobs.failed24 })}</span></>}
          </div>
        </div>
        <div className="card stat">
          <div className="label">{tr('Storage used')}</div>
          <div className="value num">{bytes(d.usage.physical)}</div>
          <div className="ledger"><span style={{ width: `${usedPct}%`, background: usedPct > 90 ? 'var(--bad)' : 'var(--brass)' }} /></div>
          <div className="foot">{tr('of {gb} GB in your subscription', { gb: d.usage.storage_gb })}</div>
        </div>
        <div className="card stat">
          <div className="label">{tr('Automatic checks')}</div>
          {d.verification?.failed ? (
            <div className="foot" style={{ marginTop: 8, color: 'var(--bad)' }}>{tr('A check found a problem.')} <Link to={`${t.base}/alerts`}>{tr('See alerts')}</Link></div>
          ) : (d.verification?.restore_verified ?? 0) + (d.verification?.sample_verified ?? 0) > 0 ? (
            <div className="foot" style={{ marginTop: 8 }}>✓ {tr('Your backups are tested automatically and can be restored.')}</div>
          ) : (
            <div className="foot" style={{ marginTop: 8 }}>{tr('Checks start automatically after the first backups.')}</div>
          )}
        </div>
      </div>

      <Card title={tr('Alerts')} actions={<Link className="btn sm ghost" to={`${t.base}/alerts`}>{tr('All alerts')}</Link>} pad={false}>
        {d.alerts.length === 0 ? (
          <Empty glyph="✓" title={tr('No alerts')} />
        ) : (
          <table>
            <tbody>
              {(d.alerts as AlertRow[]).slice(0, 5).map((a) => {
                const c = alertCopy(a, t.base);
                return (
                  <tr key={a.id}>
                    <td style={{ width: 130 }}><Badge tone={c.tone}>{c.level}</Badge></td>
                    <td><strong>{c.title}</strong><div className="small muted">{c.todo}</div></td>
                    <td className="right muted small" style={{ whiteSpace: 'nowrap' }}>{lastHappened(a)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      {!manage && <p className="muted small">{tr('You can see everything here, but only admins can make changes.')}</p>}
    </div>
  );
}
